import type { ReactElement } from 'react';
import React, { useCallback, useContext, useRef, useState } from 'react';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useTwcg2304ImportInOnboarding } from '@confluence/experiment-twcg-2304-import-in-onboarding/entry-points/useTwcg2304ImportInOnboarding.ts';

import { isBulkUpload } from '../utils/isBulkUpload.ts';
import type { ResponseContentMetadata } from '../services/DocImportService.ts';
import { useGoToDraftPage } from '../hooks/useGoToDraftPage.ts';
import { uploadFileWithDiscard } from '../services/UploadFileWithDiscard.ts';
import type { ProcessErrors } from '../config/processErrors.ts';
import { getFileNameWithoutExtension } from '../utils/getFileNameWithoutExtension.ts';
import { useGoToPage } from '../hooks/useGoToPage.ts';
import { useHandleDeleteCreatedDraft } from '../hooks/useHandleDeleteCreatedDraft.ts';
import { useHandleDeleteOnboardingBlankPage } from '../hooks/useHandleDeleteOnboardingBlankPage.ts';

import { ImportModals } from './Modals/ImportModals.tsx';
import { ImportModalTypes } from './Modals/ImportModalTypes.ts';
import { ProgressStatus } from './Modals/ProgressModal/progress-types.ts';
import { ImportStateContext, ImportStateContextProvider } from './ImportStateContextProvider.tsx';
import type {
	handleDocumentSelectType,
	handleStartStrategyType,
	ImportStrategyType,
	ParentPageData,
} from './types.ts';
import { useUploadStrategy } from './uploadStrategies/useUploadStrategy.ts';

const ImportStrategyContextComponent: React.FC<{
	hasUserAddedContent: boolean | null;
	/** Empty when the import is started without an existing draft (e.g. from the side navigation). */
	contentId: string;
	/** Null places imported content at the root of the space. */
	parentPageId: string | null;
	/** Canonical numeric space ID used by public v2 create APIs. */
	spaceId?: string | null;
	hasSourceRestrictions?: boolean;
	currentSpace: string;
	children: ({
		handleStartStrategy,
		handleDocumentSelect,
		handleDocumentSelectError,
	}) => ReactElement;
	restrictionType: string;
}> = ({
	hasUserAddedContent,
	contentId,
	parentPageId,
	spaceId,
	hasSourceRestrictions = false,
	children,
	currentSpace,
	restrictionType,
}) => {
	const importStrategyRef = useRef<React.MutableRefObject<ImportStrategyType> | null>(null);
	const goToDraftPage = useGoToDraftPage();
	const goToPage = useGoToPage();
	const {
		showModal,
		closeModal,
		docActions: { uploadDocsStart },
	} = useContext(ImportStateContext);
	const [bulkParentPageId, setBulkParentPageId] = useState<string | null>(null);
	//Discard draft should be true by default to delete the page if it's empty
	const [isDraftDiscard, setDraftDiscard] = useState<boolean>(true);
	const [createsSeparatePage, setCreatesSeparatePage] = useState<boolean>(false);
	const [importedDraftPageData, setImportedDraftPageData] =
		useState<ResponseContentMetadata | null>(null);
	const [importedPageData, setImportedPageData] = useState<ParentPageData | null>(null);
	const [elementProcessingError, setElementProcessingError] = useState<ProcessErrors | null>(null);
	const [createdDraftId, setCreatedDraftId] = useState<string | null>(null);
	const [isCreatingDraftCancelled, setIsCreatingDraftCancelled] = useState<boolean>(false);
	const [isCancelledDocumentsDeleted, setIsCancelledDocumentsDeleted] = useState<boolean>(false);
	const isLivePage = useIsCurrentPageLive();
	const { isEnabled: isUploadInOnboardingEnabled, loading: isUploadInOnboardingLoading } =
		useTwcg2304ImportInOnboarding();
	const isExperimentActive = !isUploadInOnboardingLoading && isUploadInOnboardingEnabled;
	const isOnboardingImport = contentId === '' && parentPageId === null;
	const shouldCreateAsLivePage = isLivePage || isExperimentActive;

	// When the upload-in-onboarding experiment is active, the import creates a new parent page
	// rather than replacing the blank page via the originalDraftId discard mechanism. We must
	// explicitly delete the original blank page after a successful import in that case.
	const deleteOnboardingBlankPage = useHandleDeleteOnboardingBlankPage(contentId);

	//TODO: don't use useEffect as an event handler. check isCreatingDraftCancelled after draft is created
	useHandleDeleteCreatedDraft({
		createdDraftId,
		setCreatedDraftId,
		isCreatingDraftCancelled,
		setIsCreatingDraftCancelled,
		subType: shouldCreateAsLivePage ? 'live' : '',
	});

	const runImportStrategy = useCallback(() => {
		importStrategyRef.current!.current.pickDocument();
	}, []);

	// Stable identity so strategies' registerTrigger effects (keyed on onStartStrategy)
	// do not re-run on every parent render.
	const handleStartStrategy: handleStartStrategyType = useCallback(
		(strategyRef) => {
			importStrategyRef.current = strategyRef;
			const shouldCreateSeparatePage = strategyRef.current.createsSeparatePage === true;
			setCreatesSeparatePage(shouldCreateSeparatePage);
			setDraftDiscard(!shouldCreateSeparatePage);

			if (!shouldCreateSeparatePage && hasUserAddedContent && !isExperimentActive) {
				showModal(ImportModalTypes.DISCARD);
			} else {
				runImportStrategy();
			}
		},
		[hasUserAddedContent, isExperimentActive, showModal, runImportStrategy],
	);

	const handleUploadFileWithDiscard = (
		document: File,
		options: {
			createdBulkParentPageId?: string;
			isBulkUpload?: boolean;
			signal?: AbortSignal;
		} = {},
	) => {
		const { createdBulkParentPageId, isBulkUpload, signal } = options;
		// Only onboarding imports preserve the blank page while creating a separate live page.
		const effectiveDraftDiscard = isExperimentActive && isOnboardingImport ? false : isDraftDiscard;
		return uploadFileWithDiscard({
			document,
			pageTitle: getFileNameWithoutExtension(document.name),
			parentPageId: createdBulkParentPageId || parentPageId,
			space: currentSpace,
			contentId,
			isDraftDiscard: effectiveDraftDiscard,
			importSource: importStrategyRef.current!.current.key,
			isBulkUpload,
			signal,
			...(shouldCreateAsLivePage && { subType: 'live' }),
		});
	};

	const getUploadStrategy = useUploadStrategy({
		setCreatedDraftId,
		setImportedDraftPageData,
		setElementProcessingError,
		setIsCancelledDocumentsDeleted,
		currentSpace,
		restrictionType,
		parentPageId,
		handleUploadFileWithDiscard,
		setImportedPageData,
		importStrategyRef,
		subType: shouldCreateAsLivePage ? 'live' : '',
		deleteOnboardingBlankPage,
		spaceId,
		hasSourceRestrictions,
	});

	const handleDocumentSelect: handleDocumentSelectType = async (documents) => {
		const uploadStrategy = getUploadStrategy(isBulkUpload(documents));
		//should be before ProgressModal and the validations.
		uploadDocsStart(documents);

		///////////////////// validation before ProgressModal is shown //////////////////////////
		const validatedDocuments = await uploadStrategy.validateBeforeProgress(documents);
		if (!validatedDocuments) return;
		/////////////////////////////////////////////////////////////////////////////////////////

		showModal(ImportModalTypes.PROGRESS, {
			status: ProgressStatus.IN_PROGRESS,
			...(importStrategyRef.current?.current.uploadSingle
				? { skipDeferredDraftCleanupOnCancel: true }
				: {}),
		});

		///////// handle files before unload to server. export files (google or onedrive) ///////
		const handledDocuments: File[] =
			await uploadStrategy.handleFilesBeforeUpload(validatedDocuments);
		/////////////////////////////////////////////////////////////////////////////////////////

		////////// validate files before upload to server. (validate exported files) ////////////
		const finalDocuments: File[] | false =
			await uploadStrategy.validateBeforeUpload(handledDocuments);
		if (!finalDocuments) return;
		/////////////////////////////////////////////////////////////////////////////////////////

		uploadStrategy.handleUpload(finalDocuments);
	};

	const handleDocumentSelectError = () => {
		closeModal(ImportModalTypes.PROGRESS);
		showModal(ImportModalTypes.UPLOAD_ERROR);
	};

	const goToCreatedDraftPage = () => {
		goToDraftPage(importedDraftPageData);
	};

	const goToCreatedPage = () => {
		goToPage(importedPageData);
	};

	return (
		<>
			<ImportModals
				isDraftDiscard={isDraftDiscard}
				preserveSourcePage={createsSeparatePage}
				setDraftDiscard={setDraftDiscard}
				onReimport={runImportStrategy}
				goToCreatedDraftPage={goToCreatedDraftPage}
				goToCreatedPage={goToCreatedPage}
				contentId={contentId}
				elementProcessingError={elementProcessingError}
				setElementProcessingError={setElementProcessingError}
				bulkParentPageId={bulkParentPageId}
				setBulkParentPageId={setBulkParentPageId}
				setIsCreatingDraftCancelled={setIsCreatingDraftCancelled}
				isCancelledDocumentsDeleted={isCancelledDocumentsDeleted}
			/>
			{children({
				handleStartStrategy,
				handleDocumentSelect,
				handleDocumentSelectError,
			})}
		</>
	);
};

export const ImportStrategyContext = (props: any): React.JSX.Element => (
	<ImportStateContextProvider>
		<ImportStrategyContextComponent {...props} />
	</ImportStateContextProvider>
);
